import { useState, useRef, useEffect } from 'react';

export default function AutocompleteSelect({
	options = [],
	labelKey = 'label',
	valueKey = 'value',
	placeholder = 'Selecione uma opção...',
	onChange = () => { },
	defaultValue = '',
	containerClassName = 'relative w-full',
	inputClassName = 'w-full h-full',
	mapKey = 'aci_',
	value = false,
}) {
	// Estado para armazenar o valor do input
	const [inputValue, setInputValue] = useState('');
	// Estado para controlar se a lista de sugestões está visível
	const [showSuggestions, setShowSuggestions] = useState(false);
	// Estado para armazenar o índice do item focado
	const [focusedItemIndex, setFocusedItemIndex] = useState(-1);
	// Estado para armazenar o objeto selecionado
	const [selectedItem, setSelectedItem] = useState(null);
	// Referência para a div de sugestões para manipulação do DOM
	const suggestionsRef = useRef(null);
	// Referência para o input
	const inputRef = useRef(null);
	// Ref para controlar se o defaultValue já foi processado
	const defaultProcessedRef = useRef(false);

	// Efeito para configurar o valor padrão se fornecido - executado apenas uma vez
	useEffect(() => {
		// Se não tem valor padrão ou já foi processado, não faz nada
		if (!defaultValue || defaultProcessedRef.current) return;

		const defaultItem = options.find(item =>
			String(item[labelKey]).toLowerCase() === String(defaultValue).toLowerCase() ||
			String(item[valueKey]) === String(defaultValue)
		);

		if (defaultItem) {
			setInputValue(defaultItem[labelKey]);
			setSelectedItem(defaultItem);
			// Não chamar onChange aqui para evitar loops
		}

		// Marcar como processado para evitar repetições
		defaultProcessedRef.current = true;
	}, [defaultValue, options]); // Dependências reduzidas

	// Efeito separado para verificar mudanças na lista de opções
	useEffect(() => {
		// Se temos um item selecionado, verificamos se ainda é válido com as novas opções
		if (selectedItem) {
			const stillExists = options.some(opt => opt[valueKey] === selectedItem[valueKey]);
			if (!stillExists) {
				setSelectedItem(null);
				setInputValue('');
			}
		}
	}, [options, valueKey]);

	//Effect to value changed
	useEffect(() => {
		if (value && options?.length > 0) {
			const opt = options.find(el => el[valueKey] === value) || false
			// console.log('autocomplete value:', value, opt)
			if (opt) {
				handleInputChange({
					target: {
						value: opt?.[labelKey]
					}
				}, false);
			}
		}
	}, [value])

	// Filtrar opções baseado no texto digitado
	const filteredOptions = options.filter(option =>
		String(option[labelKey]).toLowerCase().includes(inputValue.toLowerCase())
	);

	// Manipulador de mudança no input
	const handleInputChange = (e, show_suggestions = true) => {
		const value = e.target.value;
		setInputValue(value);

		if (value === '' && selectedItem) {
			setSelectedItem(null);
			//   onChange(null);
			onChange({});
		}
		if(show_suggestions){
			setShowSuggestions(true);
			setFocusedItemIndex(-1);
		}
	};

	// Manipulador de seleção de item
	const handleSelectItem = (option) => {
		// Evita atualização se for o mesmo item
		if (selectedItem && selectedItem[valueKey] === option[valueKey]) {
			setShowSuggestions(false);
			return;
		}

		setInputValue(option[labelKey]);
		setSelectedItem(option);
		setShowSuggestions(false);

		// Só chamar onChange se o item selecionado for diferente
		onChange(option);
	};

	// Toggle para exibir/ocultar sugestões ao clicar no ícone
	const toggleSuggestions = () => {
		setShowSuggestions(!showSuggestions);
		if (!showSuggestions) {
			inputRef.current.focus();
		}
	};

	// Fechar sugestões ao clicar fora
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
				inputRef.current && !inputRef.current.contains(e.target)) {
				setShowSuggestions(false);

				// Se houver um item selecionado, garantir que o input mostre o valor correto
				if (selectedItem) {
					setInputValue(selectedItem[labelKey]);
				}
				// Se o input estiver vazio, não fazer nada
				else if (inputValue.trim() !== '') {
					// Verificar se existe alguma correspondência exata
					const exactMatch = options.find(opt =>
						opt[labelKey].toLowerCase() === inputValue.toLowerCase()
					);

					if (exactMatch) {
						setSelectedItem(exactMatch);
						onChange(exactMatch);
					} else {
						// Se não houver correspondência, limpar o input
						setInputValue('');
					}
				}
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [selectedItem, inputValue, options, labelKey, onChange]);

	// Navegação pelo teclado
	const handleKeyDown = (e) => {
		if (!showSuggestions || filteredOptions.length === 0) return;

		// Seta para baixo
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setFocusedItemIndex(prevIndex =>
				prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : 0
			);
		}
		// Seta para cima
		else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setFocusedItemIndex(prevIndex =>
				prevIndex > 0 ? prevIndex - 1 : filteredOptions.length - 1
			);
		}
		// Enter
		else if (e.key === 'Enter' && focusedItemIndex >= 0) {
			e.preventDefault();
			handleSelectItem(filteredOptions[focusedItemIndex]);
		}
		// Escape
		else if (e.key === 'Escape') {
			e.preventDefault();
			setShowSuggestions(false);
			// Restaurar valor anterior se houver item selecionado
			if (selectedItem) {
				setInputValue(selectedItem[labelKey]);
			}
		}
	};

	// Destaque para texto que corresponde à pesquisa
	const highlightMatch = (text, query) => {
		if (!query) return text;

		const index = String(text).toLowerCase().indexOf(String(query).toLowerCase());
		if (index === -1) return text;

		const textStr = String(text);

		return (
			<>
				{textStr.substring(0, index)}
				<strong className="font-bold">
					{textStr.substring(index, index + query.length)}
				</strong>
				{textStr.substring(index + query.length)}
			</>
		);
	};

	return (
		<div className={containerClassName}>
			{/* Container para input e ícone */}
			<div className="relative w-full flex items-center">
				{/* Input de pesquisa */}
				<input
					ref={inputRef}
					type="text"
					value={inputValue}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					onFocus={() => {
						if (inputValue === 'TODOS') {
							setInputValue('')
						}
						setShowSuggestions(Boolean(inputValue) || options.length > 0)
					}}

					placeholder={placeholder}
					className={`${inputClassName} pr-8`} // Adicionado padding à direita para dar espaço ao ícone
				/>


				{inputValue?.length > 0 && !inputValue.match(new RegExp(placeholder, "i")) && /* x para limpar o conteudo */
					<div
						className="absolute right-9 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600"
						onClick={() => {
							// setInputValue("")
							// onChange({})
							setInputValue("");
							setSelectedItem({});
							setShowSuggestions(false);
							onChange({});
						}}
					>x</div>
				}

				{/* Ícone de seta para baixo */}
				<div
					className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600"
					onClick={toggleSuggestions}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						className={`transition-transform duration-200 ${showSuggestions ? 'rotate-180' : ''}`}
					>
						<polyline points="6 9 12 15 18 9"></polyline>
					</svg>
				</div>
			</div>

			{/* Lista de sugestões */}
			{showSuggestions && filteredOptions.length > 0 && (
				<div
					ref={suggestionsRef}
					className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
				>
					{filteredOptions.map((option, index) => (
						<div
							key={mapKey + "_" + option[valueKey]}
							onClick={() => handleSelectItem(option)}
							className={`p-3 cursor-pointer hover:bg-gray-100 ${index === focusedItemIndex ? 'bg-gray-100' : ''
								}`}
						>
							{highlightMatch(option[labelKey], inputValue)}
						</div>
					))}
				</div>
			)}
		</div>
	);
}